// @flow
import Intercom from 'react-intercom';
import React from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import { Link, Route, withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

import { withTheme, withStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import makeComponentTrashable from 'trashable-react';
import countBy from 'lodash/countBy';

import { authInstance, database } from '../../firebase';
// import { avaLogoUrl } from '../../utils/scribeUtils';
import ParticipantList from '../../components/ParticipantList';

import AvaLogoImg from '../../../assets/images/logo.png';
import ScribeMainSidebarContainer from './ScribeMainSidebarContainer';
import {
  getDefaultRoomId,
  sendResetRoomIdMessage,
  isDefaultRoomId,
} from '../../utils/ws-v1';
import getSocket from '../../utils/getSocket';
import { SMALL } from '../../utils';
import { clearSearchValue } from '../../utils/http';
// eslint-disable-next-line
import { getSnack } from '../../utils/snackbar';
import ScribeSpeakerAudioSwitchContainer from './ScribeSpeakerAudioSwitchContainer';
import AudioChunkBufferContainer from './AudioChunkBufferContainer';
import UpdateConvoMeta from './UpdateConvoMeta';
import { BackendBadge, getBadge } from './BackendBadge';
import CallListContainer from './CallListContainer';
import AddParticipant from './AddParticipant';
import FullScreenHamburger from './FullScreenHamburger';
import SelectAccuracyButton from './SelectAccuracyButton';
import { isHost } from '../../utils/status';
import electron from '../../utils/electron';

const ScribeLogo = styled.div`
  margin: 0px;
  margin-bottom: 5px;
  margin-left: 10px;
  --webkit-app-region: drag;
`;

const getConversationModeColor = (conversationMode: any) => {
  switch (conversationMode) {
    case 'public-muted':
      return 'darkgreen';
    case 'public':
      return 'darkhaki';
    case 'private':
      return 'tomato';
    case 'group':
      return 'steelblue';
    default:
      return 'red';
  }
};

// eslint-disable-next-line
const computeStatusDiff = (
  oldStatus: Object,
  newStatus: Object,
  user: Object
): Array<Object> => {
  if (isDefaultRoomId(newStatus.id)) {
    return [];
  }
  if (oldStatus.id !== newStatus.id) {
    return [];
  }
  const diff = [];
  const participants = {};
  oldStatus.participants.concat(newStatus.participants).forEach(participant => {
    const gs = participant.avaName ? `(&${participant.avaName})` : '(guest)';
    const ds = `${participant.userName} ${gs}`;
    participants[participant.avaId] = { ...participant, ds };
  });
  const oldCounts = countBy(oldStatus.participants, 'avaId');
  const newCounts = countBy(newStatus.participants, 'avaId');
  Object.keys(oldCounts).forEach(id => {
    if (!newCounts[id]) {
      diff.push({
        text: `${participants[id].ds} left the conversation.`,
        variant: 'info',
      });
    }
  });
  Object.keys(newCounts).forEach(id => {
    if (!oldCounts[id] && id !== user.avaId) {
      diff.push({
        text: `${participants[id].ds} joined the conversation.`,
        variant: 'info',
      });
    }
  });

  const oldChannelCount = countBy(oldStatus.participants, 'channel');
  const newChannelCount = countBy(newStatus.participants, 'channel');
  if (
    !newChannelCount.ios &&
    !newChannelCount.android &&
    (oldChannelCount.ios || oldChannelCount.android)
  ) {
    diff.push({
      text: 'no mobile participant to the convo!',
      variant: 'warning',
    });
  }

  return diff;
};

const styles = theme => {
  return {
    appBar: {
      boxShadow: 'none',
      borderBottom: `1px solid ${theme.palette.border}`,
    },
    signIn: {
      '&:hover': {
        color: theme.palette.text.primary,
      },
      color: theme.palette.text.primary,
      fontWeight: 700,
      marginRight: 4,
    },
    windowsTitlebarButton: {
      '&:focus': {
        outline: 'none',
      },
      background: 'transparent',
      border: 'none',
      cursor: 'default',
      fontSize: 12,
      height: '100%',
      padding: 0,
      WebkitAppearance: 'none',
      width: 46,
    },
    windowsTitlebarButtonLight: {
      '&:hover': {
        background: 'rgba(23,23,23,0.1)',
      },
      '&:active': {
        background: 'rgba(23,23,23,0.2)',
      },
      '&>svg>g': {
        fill: '#171717',
      }
    },
    windowsTitlebarButtonDark: {
      '&:hover': {
        background: 'rgba(255,255,255,0.1)',
      },
      '&:active': {
        background: 'rgba(255,255,255,0.2)',
      },
      color: '#FFFFFF',
    },
    windowsTitlebarCloseButton: {
      '&:hover': {
        background: '#E81123',
        '&>svg>g': {
          fill: '#FFFFFF',
        },
      },
      '&:active': {
        background: '#F1707A',
        '&>svg>g': {
          fill: '#171717',
        },
      },
    }
  }
}

type State = {
  status: string,
  electronWindowMaximized: boolean,
};
type Props = {
  backends: Object,
  conversationEnded: ?boolean,
  conversationStarted: ?boolean,
  classes: Object,
  currentPlatform: string,
  displayShortcuts: boolean,
  displayable: Object,
  electronCaptionMode: boolean,
  exitScribeMode: Function,
  features: Object,
  firebaseUser: FirebaseUser,
  fullScreen: boolean,
  getTranscriptHtml: Function,
  getTranscriptText: Function,
  history: Object,
  inTwilioCalls: Boolean,
  intercomDisplayed: Boolean,
  lightView: boolean,
  product: string,
  scribe: boolean,
  sidebar: boolean,
  speakerListened: boolean,
  speakers: Object,
  status: Object,
  switchPlatform: Function,
  switchWebProduct: Function,
  t: Function,
  theme: Object,
  toggleSideBar: Function,
  updateFullScreen: Function,
  user: Object,
};

const Logo = styled.img`
  width: 100px;
`;

const BadgeConnect = styled(Badge)`
  span {
    background-color: ${props => props.colorstatus};
  }
`;

class ScribeHeader extends React.Component<Props, State> {
  ref;

  constructor(props) {
    super(props);
    this.state = {
      status: '',
      electronWindowMaximized: false,
    };
  }

  getTranscriptText = () => {
    const { getTranscriptText, getTranscriptHtml } = this.props;
    getTranscriptText();
    // eslint-disable-next-line
    if (!SMALL && false) {
      getTranscriptHtml();
    }
  };

  getFirebaseInfo = () => {
    // const { currentPlatform } = this.props;
    if (!authInstance.currentUser) {
      return;
    }

    this.ref = database.ref(
      `/users/${authInstance.currentUser.uid}/captioner_information`
    );
    /* HACK - remover availability display for migration firebase -> firestore.
    if (currentPlatform === 'scribe') {
      this.ref.on('value', async snapshot => {
        const { status } = this.state;
        const availablePlatform = snapshot.val().available;
        if (status !== availablePlatform) {
          await this.setState({ status: availablePlatform });
          const emoji = this.isOnline() ? '\ud83c\udf4f' : '\ud83d\udd34';
          const notifText = `${emoji} ${emoji} ${emoji} scribe status: ${
            this.isOnline() ? 'online' : 'offline'
          } `;
          // eslint-disable-next-line no-new
          new window.Notification(notifText, {
            icon: avaLogoUrl,
            body: authInstance.currentUser.email,
          });
        }
      });
    }
    */
  };

  isOnline = () => {
    const { status } = this.state;
    const { currentPlatform, backends } = this.props;
    const backend = getBadge(backends, currentPlatform);
    switch (backend.name) {
      case 'playground':
        return status === 'playground';
      case 'production':
        return status === 'production';
      default:
        return false;
    }
  };

  updateFullScreen = e => {
    e.preventDefault();
    const { updateFullScreen, fullScreen, toggleSideBar } = this.props;
    updateFullScreen(!fullScreen);
    toggleSideBar();
  };

  componentWillUnmount = () => {
    if (this.ref && this.ref.off) {
      this.ref.off();
    }
  };

  componentDidUpdate = () => {
    const { firebaseUser, product } = this.props;
    if (firebaseUser && product === 'scribe') {
      this.getFirebaseInfo();
    }
  };

  async restartConversation() {
    const {
      exitScribeMode,
      history,
      scribe,
      switchPlatform,
      switchWebProduct,
      user,
    } = this.props;
    clearSearchValue(window, 'token');
    exitScribeMode();
    sendResetRoomIdMessage(await getSocket(), getDefaultRoomId(user.id));
    if (scribe) {
      switchWebProduct('scribeDashboard');
      await switchPlatform('web', history);
    }
  }

  render() {
    const { isOnline } = this;
    const {
      backends,
      classes,
      conversationEnded,
      conversationStarted,
      currentPlatform,
      displayShortcuts,
      displayable,
      electronCaptionMode,
      features,
      firebaseUser,
      fullScreen,
      inTwilioCalls,
      intercomDisplayed,
      lightView,
      scribe,
      sidebar,
      speakerListened,
      speakers,
      status,
      t,
      theme,
      toggleSideBar,
      user,
    } = this.props;
    const { electronWindowMaximized } = this.state;
    if (lightView) return null;
    const participants = (status || {}).participants || [];
    const transcriptOwnersId = (status || {}).transcriptOwners || [];
    const backgroundColor = theme.palette.secondary[theme.palette.type];
    const lineColor = theme.palette.divider;
    const backend = getBadge(backends, currentPlatform);
    let isMac;
    let currentWindow;
    if (window.isElectron) {
      const { remote } = electron;
      isMac = remote.process.platform === 'darwin';
      currentWindow = remote.getCurrentWindow();
    }

    return (
      <div>
        <Intercom
          appID="eko73i1t"
          email={firebaseUser ? firebaseUser.email : undefined}
          user_id={user ? user.avaId : undefined}
          hide_default_launcher={!intercomDisplayed || fullScreen}
          desktop_app={!!window.isElectron}
        />
        {window.isElectron && !electronCaptionMode && (
          <div
            className="window-frame-padding"
            style={{ height: isMac ? 28 : 32 }} />
        )}
        <AppBar
          className={classes.appBar}
          position="fixed"
          style={{
            display: fullScreen ? 'none' : undefined,
            zIndex: 1300,
            WebkitAppRegion: 'no-drag',
          }}
        >
          {window.isElectron && isMac && !electronCaptionMode && (
            <div style={{
              alignItems: 'center',
              background: backgroundColor,
              borderBottom: 'solid 1px',
              borderBottomColor: lineColor,
              height: 28,
              WebkitAppRegion: 'drag',
            }} />
          )}
          {window.isElectron && !isMac && !electronCaptionMode && (
            <div
              id="title-bar"
              style={{
                alignItems: 'center',
                background: backgroundColor,
                borderBottom: 'solid 1px',
                borderBottomColor: lineColor,
                display: 'flex',
                justifyContent: 'flex-end',
                height: 32,
                WebkitAppRegion: 'drag',
              }}
            >
              <div
                id="title-bar-btns"
                style={{
                  height: '100%',
                  WebkitAppRegion: 'no-drag',
                }}>
                <button
                  id="min-btn"
                  type="button"
                  className={
                    `${classes.windowsTitlebarButton} ${theme.palette.type === 'light' ?
                    classes.windowsTitlebarButtonLight
                    : classes.windowsTitlebarButtonDark}`}
                  onClick={() => {currentWindow.minimize()}}
                >
                  <svg width="46" height="32" viewBox="0 0 46 32">
                    <g id="minimize" stroke="none" strokeWidth="1" fillRule="evenodd">
                      <polygon fillRule="nonzero" points="16 17 30 17 30 16 16 16" />
                    </g>
                  </svg>
                </button>
                <button
                  id="max-btn"
                  type="button"
                  className={
                    `${classes.windowsTitlebarButton} ${theme.palette.type === 'light' ?
                    classes.windowsTitlebarButtonLight
                    : classes.windowsTitlebarButtonDark}`}
                  onClick={() => {
                    if (electronWindowMaximized) {
                      currentWindow.unmaximize();
                    } else {
                      currentWindow.maximize();
                    }
                    this.setState({
                      electronWindowMaximized: !electronWindowMaximized,
                    });
                  }}
                >
                  {electronWindowMaximized ? (
                    <svg width="46" height="32" viewBox="0 0 46 32">
                      <g id="unmaximize" stroke="none" strokeWidth="1" fillRule="evenodd">
                        <path d="M18.3310547,23 L30,23 L30,11.3310547 L27.6689453,11.3310547 L27.6689453,9 L16,9 L16,20.6689453 L18.3310547,20.6689453 L18.3310547,23 Z M26.5,10.1689453 L26.5,19.5 L17.1689453,19.5 L17.1689453,10.1689453 L26.5,10.1689453 Z M28.8310547,12.5 L28.8310547,21.8310547 L19.5,21.8310547 L19.5,20.6689453 L27.6689453,20.6689453 L27.6689453,12.5 L28.8310547,12.5 Z" fillRule="nonzero" />
                      </g>
                    </svg>
                  ) : (
                    <svg width="46" height="32" viewBox="0 0 46 32">
                      <g id="close" fillRule="evenodd" stroke="none" strokeWidth="1">
                        <path fillRule="nonzero" d="M28.527 22a.473.473 0 00.473-.473V10.473a.474.474 0 00-.473-.473H17.473a.473.473 0 00-.473.473v11.054a.474.474 0 00.473.473h11.054zM17.923 11h10.154v10H17.923V11z" />
                      </g>
                    </svg>
                  )}
                </button>
                <button
                  id="close-btn"
                  type="button"
                  className={
                    `${classes.windowsTitlebarButton} ${classes.windowsTitlebarCloseButton} ${theme.palette.type === 'light' ?
                    classes.windowsTitlebarButtonLight
                    : classes.windowsTitlebarButtonDark}`}
                  onClick={() => {currentWindow.close()}}
                >
                  <svg width="46" height="32" viewBox="0 0 46 32">
                    <g id="close" stroke="none" strokeWidth="1" fillRule="evenodd">
                      <polygon fillRule="nonzero" points="23.2114478 15.3240981 28.5355459 10 29.4228956 10.8873497 24.0987975 16.2114478 29.4228956 21.5355459 28.5355459 22.4228956 23.2114478 17.0987975 17.8873497 22.4228956 17 21.5355459 22.3240981 16.2114478 17 10.8873497 17.8873497 10" />
                    </g>
                  </svg>
                </button>
              </div>
            </div>
          )}
          <Toolbar
            style={{
              background: backgroundColor,
              height: 67,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {firebaseUser && (
              // eslint-disable-next-line
              <div
                // {...getTogglerProps()}
                role="button"
                onClick={toggleSideBar}
                style={{
                  outline: 'none',
                }}
              >
                <i
                  className="material-icons"
                  style={{
                    fontSize: 30,
                    marginRight: 20,
                    cursor: 'pointer',
                    color: theme.palette.text.primary,
                  }}
                >
                  menu
                </i>
              </div>
            )}
            <Hidden xsDown>
              {currentPlatform === 'scribe' && (
                <ScribeLogo key={1}>
                  <Typography variant="h4">Scribe FFT (Roger Voice)</Typography>
                </ScribeLogo>
              )}
              {currentPlatform !== 'scribe' && (
                <a href="https://web.ava.me/">
                  <Logo src={AvaLogoImg} alt="ava logo" />
                </a>
              )}
            </Hidden>
            {currentPlatform === 'scribe' &&
              conversationStarted &&
              conversationEnded && (
              <Hidden only={['xs', 'sm']}>
                <BackendBadge
                  className
                  style={{
                    backgroundColor: theme.palette.error.light,
                  }}
                >
                  Conversation is over
                </BackendBadge>
              </Hidden>
            )}
            <ScribeSpeakerAudioSwitchContainer />
            <CallListContainer />
            {currentPlatform === 'web' && <AudioChunkBufferContainer />}
            {status && status.id && user && !isDefaultRoomId(status.id) && (
              <ParticipantList
                scribe={features.scribe && scribe}
                theme={theme}
                speakers={speakers}
                roomSpeakers={status.speakers}
                participants={participants}
                transcriptOwnersId={transcriptOwnersId}
                status={status}
                displayShortcuts={displayShortcuts}
                speakerListened={speakerListened}
                share
              />
            )}
            <div style={{ flexGrow: 1 }} />
            {(displayable.dev || currentPlatform === 'scribe') && (
              <Hidden xsDown>
                <BackendBadge
                  style={{
                    backgroundColor: backend.backgroundColor,
                  }}
                >
                  {backend.name}
                  {features.ok} {displayable.dev}
                </BackendBadge>
              </Hidden>
            )}
            {scribe && status && status.conversationMode && (
              <BackendBadge
                style={{
                  backgroundColor: getConversationModeColor(
                    status.conversationMode
                  ),
                }}
              >
                {status.conversationMode}
              </BackendBadge>
            )}
            {scribe && status && status.title !== undefined && (
              <UpdateConvoMeta
                title={status.title}
                roLink={status.links ? status.links.ro : ''}
              />
            )}
            <Hidden xsDown>
              <div style={{ flexGrow: 1 }} />
              <div style={{ flexGrow: 1 }} />
            </Hidden>
            <div style={{ flexGrow: 1 }} />
            {
              status &&
              status.id &&
              !isDefaultRoomId(status.id) &&
              user &&
              isHost(status, user) && (
                <SelectAccuracyButton status={status} user={user} />
            )}
            {status &&
              status.id &&
              user &&
              !status.past &&
              transcriptOwnersId &&
              transcriptOwnersId.length > 0 &&
              !isDefaultRoomId(status.id) && (
                <>
                  {!inTwilioCalls && <AddParticipant scribe={scribe} status={status} user={user} />}
                </>
            )}
            {firebaseUser && (
              <Hidden only={['xs', 'sm']}>
                {currentPlatform === 'scribe' && (
                  <div css={{ marginRight: 10 }}>
                    <BadgeConnect
                      colorstatus={isOnline() ? '#4caf50' : '#f44336'}
                      variant="dot"
                    >
                      <Avatar
                        src={firebaseUser.photoURL}
                        alt="profile_photo"
                        style={{ width: 36, height: 36 }}
                      />
                    </BadgeConnect>{' '}
                  </div>
                )}
              </Hidden>
            )}
            {!user && (
              <Route
                exact
                path="/(web|scribe)"
                render={() => <Link to="/web/login" className={classes.signIn}>{t('signIn')}</Link>} />
            )}
          </Toolbar>
        </AppBar>
        {!electronCaptionMode && (
          <FullScreenHamburger
            updateFullScreen={e => this.updateFullScreen(e)}
            fullScreen={fullScreen}
          />
        )}
        <ScribeMainSidebarContainer
          sidebarShown={sidebar}
          allowSaving={!!status.confidential && status.confidential.allowTranscriptsSaving && !!user.features && !user.features["forbid-conversation-saving"]}
        />
      </div>
    );
  }
}

// $FlowFixMe
export default withRouter(
  withTheme(withStyles(styles)(makeComponentTrashable(withTranslation()(ScribeHeader))))
);
